/**
 * App Version — automatisch oplopend bij elke deploy.
 *
 * De backend berekent de versie uit git commit count (loopt automatisch op bij
 * elke publish). We fetchen 'em eenmalig bij app-start en cachen de waarde.
 *
 * FALLBACK: als de backend niet bereikbaar is (bv. tijdens inloggen zonder
 * internet), toont de UI de laatst bekende versie uit localStorage.
 */
import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'waszbase_app_version';
const FALLBACK = '2.6';

// Direct bruikbaar: eerst waarde uit cache zodat eerste render een versie toont.
export let APP_VERSION = (() => {
  try { return localStorage.getItem(CACHE_KEY) || FALLBACK; } catch { return FALLBACK; }
})();

let _fetchPromise = null;
export const refreshAppVersion = async () => {
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = (async () => {
    try {
      const resp = await fetch(`${API_URL}/api/version`, { cache: 'no-cache' });
      if (!resp.ok) throw new Error('version endpoint failed');
      const data = await resp.json();
      if (data?.version) {
        APP_VERSION = data.version;
        try { localStorage.setItem(CACHE_KEY, data.version); } catch { /* ignore */ }
        return data;
      }
    } catch (e) {
      // Non-blocking — fallback-versie wordt getoond
    } finally {
      _fetchPromise = null;
    }
    return null;
  })();
  return _fetchPromise;
};

/** Hook: component re-rendert zodra versie binnen is. */
export const useAppVersion = () => {
  const [version, setVersion] = useState(APP_VERSION);
  useEffect(() => {
    let mounted = true;
    refreshAppVersion().then((data) => {
      if (mounted && data?.version) setVersion(data.version);
    });
    return () => { mounted = false; };
  }, []);
  return version;
};

/** Kleine component die altijd de actuele versie toont. */
export const AppVersionText = () => useAppVersion();
